import { MapBuilder, px, type Decor, type Light, type MapDef } from '../mapdef';

/**
 * Hualien city (花蓮市) and Ji'an (吉安), roughly as they lie on the real map:
 * - north-west: Hualien Station with its plaza, taxis and the tourist shuttle (railway behind it);
 * - north: the Cultural and Creative Park (old winery) and, on the hill over the Meilun River
 *   mouth, the Pine Garden (松園別館);
 * - centre: downtown shophouses with the snack street (mochi, bomb pancakes, wontons) and, east of
 *   Zhongzheng Road, Dongdamen Night Market (東大門夜市) with its themed alleys;
 * - east: the seaside hotel, Beibin Park and the coastal bike path along the Pacific;
 * - south: quiet Ji'an with paddy fields, the Container Coffee shop and Qingxiu Temple (慶修院).
 *
 * Big landmarks are bottom-anchored decor drawn in 80 px slices (`variant` = slice, `h` = total
 * height) over 'O' footprints, so the player walks behind them and they never pop out of view.
 * Ground chars of this package: 'a' bike path, 'i' gravel/stone paving (walkable), 'j' paddy (solid).
 */
const W = 96, H = 64;
const b = new MapBuilder(W, H, 'g');

// --- Edges, the Pacific, the Meilun River ------------------------------------------------------
b.fill(0, 0, 1, H, 'h').fill(0, H - 1, 86, 1, 'h');
for (let y = 0; y < H; y++) {
  const shore = 86 + (Math.sin(y / 5) > 0.55 ? 1 : 0);
  b.fill(86, y, shore - 86 + 2, 1, 'o');
  b.fill(shore + 2, y, W - shore - 2, 1, 'Z');
}
b.fill(40, 0, 46, 3, 'q');
b.fill(40, 3, 46, 2, 'a'); // Meilun riverside bike path…
b.fill(84, 3, 2, H - 4, 'a'); // …joining the coastal bike path

// --- Hualien Station (north-west) --------------------------------------------------------------
b.fill(1, 0, 38, 1, 'h').fill(1, 1, 38, 2, 'T');
b.fill(1, 3, 34, 9, 's');
b.fill(8, 4, 20, 8, 'O'); // the station building
b.fill(1, 12, 34, 9, 'i'); // the plaza (granite slabs, see groundHlcity.ts)
b.fill(35, 0, 5, 3, 'h');

// --- Roads: Zhongzheng Road (N-S) and two avenues (E-W) ------------------------------------------
b.fill(35, 3, 1, 46, 's').fill(39, 5, 1, 44, 's').fill(36, 3, 3, 46, 'r');
b.fill(1, 21, 83, 1, 's').fill(1, 22, 83, 3, 'r').fill(1, 23, 83, 1, 'l').fill(1, 25, 83, 1, 's');
b.fill(1, 45, 83, 1, 's').fill(1, 46, 83, 2, 'r').fill(1, 48, 83, 1, 's');
b.fill(35, 22, 5, 3, 'r').fill(35, 46, 5, 2, 'r');
b.fill(30, 22, 3, 3, '=').fill(36, 19, 3, 2, '=').fill(36, 26, 3, 2, '=').fill(52, 22, 3, 3, '=').fill(70, 22, 3, 3, '=').fill(36, 43, 3, 2, '=');

// --- Cultural and Creative Park (the old winery) -----------------------------------------------
b.fill(40, 5, 18, 16, 'g');
b.fill(41, 6, 8, 5, 'O').fill(50, 6, 8, 5, 'O').fill(50, 14, 7, 4, 'O');
b.fill(40, 11, 18, 3, 'p').fill(49, 6, 1, 15, 'p').fill(40, 18, 18, 3, 'p');

// --- Pine Garden on its hill over the river mouth ----------------------------------------------
b.fill(58, 5, 1, 16, 'h').fill(59, 5, 25, 16, 'g');
b.fill(62, 15, 19, 3, 'i').fill(70, 18, 2, 3, 'i').fill(78, 6, 5, 4, 'i').fill(80, 10, 1, 5, 'i');
b.fill(65, 8, 12, 7, 'O');
b.fill(60, 6, 3, 2, 'G').fill(61, 18, 4, 2, 'G').fill(75, 18, 4, 2, 'G');

// --- Downtown: shophouses and the snack street -------------------------------------------------
for (const y of [26, 36]) for (const [x, w] of [[2, 10], [14, 10], [26, 8]] as const) b.fill(x, y, w, 4, 'O');
b.fill(1, 26, 1, 4, 'p').fill(12, 26, 2, 4, 'p').fill(24, 26, 2, 4, 'p').fill(34, 26, 1, 4, 'p');
b.fill(1, 36, 1, 4, 'p').fill(12, 36, 2, 4, 'p').fill(24, 36, 2, 4, 'p').fill(34, 36, 1, 4, 'p');
b.fill(1, 30, 34, 6, 'Q').fill(1, 40, 34, 5, 'Q');

// --- Dongdamen Night Market --------------------------------------------------------------------
b.fill(40, 26, 31, 19, 'n');
b.fill(71, 26, 1, 19, 'p');

// --- The seaside hotel and Beibin Park ---------------------------------------------------------
b.fill(72, 26, 12, 19, 'g');
b.fill(73, 28, 9, 8, 'O');
b.set(77, 35, 'D');
b.fill(72, 36, 12, 2, 's').fill(72, 26, 12, 2, 's').fill(76, 38, 2, 7, 'p').fill(72, 41, 12, 1, 'p');

// --- Ji'an (south): paddies, Container Coffee, Qingxiu Temple, the village ---------------------
b.fill(1, 49, 20, 14, 'j');
b.fill(1, 49, 20, 1, 'p').fill(10, 50, 1, 13, 'p').fill(1, 56, 20, 1, 'p');
b.fill(21, 49, 17, 14, 's');
b.fill(21, 49, 17, 1, 'h').fill(22, 50, 14, 5, 'O');
b.fill(21, 60, 17, 3, 'g').fill(26, 61, 6, 2, 'G');
b.fill(38, 49, 1, 14, 'p');
b.fill(39, 49, 17, 14, 'h').fill(40, 50, 15, 13, 'i');
b.fill(44, 50, 7, 5, 'O').fill(44, 50, 7, 1, 'h').set(44, 51, 'h').set(50, 51, 'h');
b.fill(39, 58, 1, 3, 'i').fill(55, 58, 1, 3, 'i');
b.fill(40, 50, 3, 2, 'G').fill(52, 50, 3, 2, 'G');
b.fill(56, 49, 2, 14, 'p');
b.fill(58, 49, 26, 14, 's');
for (const [x, y, w] of [[59, 49, 6], [66, 49, 6], [73, 49, 9], [59, 56, 6], [66, 56, 6], [73, 56, 6]] as const) b.fill(x, y, w, 4, 'O');
b.fill(80, 56, 4, 4, 'g');

// --- Decor -------------------------------------------------------------------------------------
const at = (tx: number, ty: number) => ({ x: px(tx), y: px(ty) });
const SLICE = 80;
/** A big landmark over an 'O' footprint: cx/bottom in px, drawn in 80 px slices. */
function big(kind: Decor['kind'], cx: number, bottom: number, w: number, h: number): Decor[] {
  const n = Math.max(1, Math.ceil((h - 90) / SLICE) + 1);
  return Array.from({ length: n }, (_, k) => ({ kind, x: cx, y: bottom - SLICE * k, w, h, variant: k }));
}
const tree = (tx: number, ty: number, kind: 'tree' | 'cherry' | 'pine' = 'tree'): Decor => ({ kind, x: px(tx) + 8, y: px(ty) + 12, solid: { x: px(tx) + 2, y: px(ty) + 6, w: 12, h: 6 } });
const lamp = (tx: number, ty: number): Decor => ({ kind: 'lamp', x: px(tx) + 8, y: px(ty) + 12, solid: { x: px(tx) + 6, y: px(ty) + 9, w: 4, h: 3 } });
const scooter = (x: number, y: number, v: number): Decor => ({ kind: 'scooter', x, y, variant: v % 2 });
const pine = (tx: number, ty: number, v: number): Decor => ({ kind: 'hc_ryukyuPine', x: px(tx) + 8, y: px(ty) + 12, w: 72, variant: v, solid: { x: px(tx) + 3, y: px(ty) + 6, w: 10, h: 6 } });
const betel = (tx: number, ty: number, v = 0): Decor => ({ kind: 'hc_betel', x: px(tx) + 8, y: px(ty) + 14, w: 40, variant: v, solid: { x: px(tx) + 5, y: px(ty) + 10, w: 6, h: 4 } });
const palm = (tx: number, ty: number, v = 0): Decor => ({ kind: 'hc_palm', x: px(tx) + 8, y: px(ty) + 14, w: 56, variant: v, solid: { x: px(tx) + 5, y: px(ty) + 10, w: 6, h: 4 } });

const decor: Decor[] = [
  // Station: the Taroko Express at the platform, the station hall, the plaza.
  { kind: 'hc_train', x: px(19), y: px(3), w: 560 },
  ...big('hc_station', px(18), px(12), 320, 128),
  { kind: 'hc_amisPillar', x: px(13), y: px(13) + 8, w: 20, solid: { x: px(13) - 4, y: px(13) + 4, w: 8, h: 4 } },
  { kind: 'hc_amisPillar', x: px(23), y: px(13) + 8, w: 20, variant: 1, solid: { x: px(23) - 4, y: px(13) + 4, w: 8, h: 4 } },
  { kind: 'hualienSign', x: px(6), y: px(15), solid: { x: px(4), y: px(15) - 4, w: px(4), h: 4 } },
  { kind: 'planter', ...at(9, 17), solid: { x: px(8) + 4, y: px(17) - 8, w: 24, h: 8 } },
  { kind: 'planter', ...at(27, 17), solid: { x: px(26) + 4, y: px(17) - 8, w: 24, h: 8 } },
  ...[[1, 17], [30, 12], [33, 12]].map(([tx, ty]) => palm(tx, ty, tx)),
  ...[3, 5, 7].map((tx, i): Decor => ({ kind: 'hc_taxi', x: px(tx) + 8, y: px(20) + 4, w: 30, variant: i, solid: { x: px(tx) - 4, y: px(19) + 10, w: 24, h: 10 } })),
  { kind: 'hc_taxiSign', x: px(1) + 8, y: px(20), w: 16 },
  { kind: 'hc_bus', x: px(20) + 8, y: px(20) + 6, w: 110, solid: { x: px(17), y: px(19) + 8, w: 104, h: 14 } },
  { kind: 'hc_bus', x: px(26) + 12, y: px(20) + 6, w: 110, variant: 1, solid: { x: px(23) + 4, y: px(19) + 8, w: 104, h: 14 } },
  { kind: 'bikeRack', x: px(3), y: px(9) },
  { kind: 'bikeRack', x: px(31), y: px(9) },
  { kind: 'vendingDeco', x: px(33), y: px(5) + 4, solid: { x: px(32) + 8, y: px(5) - 4, w: 14, h: 8 } },
  { kind: 'vendingDeco', x: px(2), y: px(5) + 4, solid: { x: px(1) + 8, y: px(5) - 4, w: 14, h: 8 } },
  { kind: 'trashBin', x: px(15), y: px(15) },
  { kind: 'trashBin', x: px(22), y: px(15) },
  { kind: 'stationBench', ...at(4, 7) },
  { kind: 'stationBench', ...at(31, 7) },
  lamp(11, 18), lamp(25, 18), lamp(1, 13), lamp(34, 13),

  // Cultural and Creative Park.
  { kind: 'hc_warehouse', x: px(45), y: px(11), w: 128 },
  { kind: 'hc_warehouse2', x: px(54), y: px(11), w: 128 },
  { kind: 'hc_warehouse3', x: px(53) + 8, y: px(18), w: 112 },
  { kind: 'hc_artCube', x: px(44), y: px(16), w: 40, solid: { x: px(43), y: px(16) - 10, w: 32, h: 10 } },
  tree(41, 14), tree(47, 15), tree(41, 17, 'cherry'), tree(57, 13), tree(57, 19),
  { kind: 'hc_bench', x: px(46), y: px(13) + 4, w: 28, solid: { x: px(45) + 2, y: px(13), w: 28, h: 4 } },

  // Pine Garden.
  ...big('hc_pineGarden', px(71), px(15), 192, 120),
  pine(60, 9, 0), pine(63, 6, 1), pine(61, 13, 2), pine(78, 12, 0), pine(81, 16, 1), pine(66, 18, 2), pine(73, 19, 1), pine(76, 6, 2), pine(68, 5, 0), pine(83, 19, 2),

  // Downtown shophouse rows (騎樓 arcades) and the snack street.
  ...[2, 14, 26].flatMap((tx, i) => [
    { kind: 'hc_shops' as const, x: px(tx) + (i === 2 ? 64 : 80), y: px(30), w: i === 2 ? 128 : 160, variant: i },
    { kind: 'hc_shops' as const, x: px(tx) + (i === 2 ? 64 : 80), y: px(40), w: i === 2 ? 128 : 160, variant: 3 + i },
  ]),
  { kind: 'hc_shrine', x: px(5), y: px(41) + 4, w: 28, solid: { x: px(4), y: px(40) + 10, w: 32, h: 10 } },
  { kind: 'foodTable', x: px(9), y: px(35), w: 40, solid: { x: px(8), y: px(35) - 8, w: 40, h: 8 } },
  { kind: 'foodTable', x: px(21), y: px(35), w: 40, solid: { x: px(20), y: px(35) - 8, w: 40, h: 8 } },
  ...[[7, 35], [11, 35], [19, 35], [23, 35]].map(([tx, ty]): Decor => ({ kind: 'stool', x: px(tx) + 6, y: px(ty) })),
  ...[3, 6, 9, 16, 19, 28, 31].map((tx, i) => scooter(px(tx), px(44) + 6, i)),
  ...[27, 30].map((tx, i) => scooter(px(tx), px(34) + 10, i + 1)),
  { kind: 'hc_tourGroup', x: px(26), y: px(33), w: 60 },
  // The street in front of the second row: planters, trees, a bus stop, vending machines.
  ...[9, 21, 31].map((tx) => tree(tx, 42)),
  ...[3, 15, 27].map((tx): Decor => ({ kind: 'planter', x: px(tx), y: px(43), solid: { x: px(tx) - 12, y: px(43) - 8, w: 24, h: 8 } })),
  { kind: 'vendingDeco', x: px(18), y: px(41) + 4, solid: { x: px(17) + 8, y: px(41) - 4, w: 14, h: 8 } },
  { kind: 'vendingDeco', x: px(19), y: px(41) + 4, solid: { x: px(18) + 8, y: px(41) - 4, w: 14, h: 8 } },
  { kind: 'hc_bench', x: px(12) + 8, y: px(43) + 4, w: 28, solid: { x: px(12), y: px(43), w: 28, h: 4 } },
  { kind: 'hc_bench', x: px(24) + 8, y: px(43) + 4, w: 28, solid: { x: px(24), y: px(43), w: 28, h: 4 } },
  { kind: 'bikeRack', x: px(33), y: px(42) },
  { kind: 'trashBin', x: px(7), y: px(41) + 4 },
  lamp(1, 30), lamp(34, 30), lamp(1, 40), lamp(34, 40),
  { kind: 'trafficLight', ...at(35, 21) },
  { kind: 'trafficLight', ...at(39, 25) },
  { kind: 'trafficLight', ...at(35, 45) },

  // Street trees along the two big roads.
  ...[56, 60, 66, 76, 82].map((tx) => tree(tx, 21)),
  ...[44, 50, 64, 74, 80].map((tx) => lamp(tx, 25)),
  ...[3, 9, 15, 21, 27, 33, 42, 50, 58, 66].map((tx) => tree(tx, 48)),
  ...[6, 18, 30, 46, 62, 78].map((tx) => lamp(tx, 45)),

  // Dongdamen Night Market.
  { kind: 'hc_gate', x: px(55) + 8, y: px(27) + 2, w: 140, solid: { x: px(55) - 57, y: px(27) - 4, w: 10, h: 6 } },
  { kind: 'hc_gatePost', x: px(59) + 8, y: px(27) + 2, w: 10, solid: { x: px(55) + 63, y: px(27) - 4, w: 10, h: 6 } },
  { kind: 'hc_alleySign', x: px(52) + 8, y: px(31) - 2, w: 20, variant: 1, solid: { x: px(52) + 5, y: px(31) - 5, w: 6, h: 3 } },
  { kind: 'hc_alleySign', x: px(58) + 8, y: px(31) - 2, w: 20, variant: 0, solid: { x: px(58) + 5, y: px(31) - 5, w: 6, h: 3 } },
  { kind: 'hc_alleySign', x: px(52) + 8, y: px(37) - 2, w: 20, variant: 2, solid: { x: px(52) + 5, y: px(37) - 5, w: 6, h: 3 } },
  { kind: 'hc_alleySign', x: px(40) + 8, y: px(44) + 12, w: 20, variant: 3, solid: { x: px(40) + 5, y: px(44) + 9, w: 6, h: 3 } },
  { kind: 'hc_stage', x: px(64) + 8, y: px(36) + 2, w: 112, solid: { x: px(61), y: px(35) + 4, w: 112, h: 32 } },
  { kind: 'hc_carvedPost', x: px(71) - 2, y: px(31) + 4, w: 16, solid: { x: px(70) + 9, y: px(31), w: 10, h: 4 } },
  ...[[42, 41], [47, 43], [52, 41], [44, 44], [50, 44]].map(([tx, ty]): Decor => ({ kind: 'foodTable', x: px(tx), y: px(ty), w: 40, solid: { x: px(tx) - 20, y: px(ty) - 8, w: 40, h: 8 } })),
  ...[[40, 41], [44, 41], [45, 43], [49, 43], [50, 41], [54, 41]].map(([tx, ty]): Decor => ({ kind: 'stool', x: px(tx) + 4, y: px(ty) })),
  ...[29, 34, 39, 44].map((ty, i): Decor => ({ kind: 'lanternString', x: px(52), y: px(ty) + 8, w: 96, variant: i })),
  { kind: 'lanternString', x: px(40), y: px(33) + 8, w: 192, variant: 2 },
  { kind: 'lanternString', x: px(58), y: px(33) + 8, w: 192, variant: 1 },
  { kind: 'lanternString', x: px(40), y: px(39) + 8, w: 192, variant: 3 },
  { kind: 'hc_crowd', x: px(55), y: px(31) + 8, w: 60, variant: 0 },
  { kind: 'hc_crowd', x: px(55), y: px(38) + 8, w: 60, variant: 1 },
  { kind: 'hc_crowd', x: px(46), y: px(33) + 2, w: 60, variant: 2 },
  { kind: 'hc_crowd', x: px(64), y: px(32) + 8, w: 60, variant: 3 },
  { kind: 'hc_crowd', x: px(68), y: px(41), w: 60, variant: 4 },
  { kind: 'hc_crowd', x: px(55), y: px(41) + 4, w: 60, variant: 5 },
  ...[[57, 43], [61, 43], [65, 43], [41, 39]].map(([tx, ty], i): Decor => ({ kind: 'hc_stallDeco', x: px(tx) + 24, y: px(ty) + 16, w: 56, variant: i, solid: { x: px(tx) + 2, y: px(ty) + 4, w: 44, h: 12 } })),
  { kind: 'trashBin', x: px(70) + 4, y: px(44) + 4 },
  { kind: 'trashBin', x: px(40) + 4, y: px(36) + 4 },

  // Seaside hotel, Beibin Park, the coastal path.
  ...big('hc_hotel', px(77) + 8, px(36), 144, 140),
  ...[[73, 40], [81, 39], [74, 43], [80, 43]].map(([tx, ty]) => palm(tx, ty, tx)),
  ...[73, 81].map((tx) => palm(tx, 26, tx + 1)),
  { kind: 'hc_bench', x: px(78) + 8, y: px(40) + 4, w: 28, solid: { x: px(78), y: px(40), w: 28, h: 4 } },
  { kind: 'hc_bench', x: px(74) + 8, y: px(38) + 4, w: 28, solid: { x: px(74), y: px(38), w: 28, h: 4 } },
  { kind: 'hc_railing', x: px(87), y: px(13) + 4, w: 30 },
  { kind: 'hc_railing', x: px(87), y: px(31) + 4, w: 30 },
  { kind: 'hc_railing', x: px(87), y: px(53) + 4, w: 30 },
  { kind: 'hc_bikeSign', x: px(83), y: px(46) - 2, w: 30, variant: 1 },
  { kind: 'bikeRack', x: px(82), y: px(28) },
  lamp(83, 14), lamp(83, 22), lamp(83, 34), lamp(83, 50), lamp(83, 58), lamp(72, 36), lamp(82, 36),

  // Ji'an: paddies with betel-nut palms, Container Coffee, Qingxiu Temple, the village.
  ...[[3, 52], [7, 53], [12, 51], [16, 52], [4, 58], [9, 60], [13, 59], [18, 61], [19, 54], [2, 61]].map(([tx, ty], i) => betel(tx, ty, i)),
  { kind: 'hc_scarecrow', x: px(15) + 8, y: px(57) + 14, w: 20 },
  { kind: 'hc_egret', x: px(6) + 4, y: px(55) + 12, w: 12 },
  { kind: 'hc_egret', x: px(17), y: px(60) + 6, w: 12, variant: 1 },
  ...big('hc_container', px(29), px(55), 224, 112),
  { kind: 'hc_umbrellas', x: px(23) + 8, y: px(58), w: 40, solid: { x: px(22) + 2, y: px(58) - 6, w: 28, h: 6 } },
  { kind: 'hc_umbrellas', x: px(34) + 8, y: px(58), w: 40, variant: 1, solid: { x: px(33) + 2, y: px(58) - 6, w: 28, h: 6 } },
  { kind: 'parkedCar', x: px(32), y: px(60) - 8, variant: 1 },
  ...[22, 24, 26].map((tx, i) => scooter(px(tx), px(60) + 6, i)),
  ...big('hc_temple', px(47) + 8, px(55), 112, 84),
  ...[0, 1, 2, 3].flatMap((k): Decor[] => [
    { kind: 'hc_buddhas', x: px(42) + 8, y: px(56 + k * 2) + 8, w: 56, variant: k },
    { kind: 'hc_buddhas', x: px(52) + 8, y: px(56 + k * 2) + 8, w: 56, variant: k + 4 },
  ]),
  ...[56, 59, 62].flatMap((ty): Decor[] => [
    { kind: 'hc_toro', x: px(45) + 8, y: px(ty) + 4, w: 12, solid: { x: px(45) + 4, y: px(ty), w: 8, h: 4 } },
    { kind: 'hc_toro', x: px(49) + 8, y: px(ty) + 4, w: 12, solid: { x: px(49) + 4, y: px(ty), w: 8, h: 4 } },
  ]),
  { kind: 'hc_basin', x: px(42), y: px(54) + 4, w: 24, solid: { x: px(41) + 4, y: px(54) - 4, w: 24, h: 8 } },
  pine(40, 53, 1), pine(53, 53, 2),
  { kind: 'hc_house', x: px(62), y: px(53), w: 96, variant: 0 },
  { kind: 'hc_house', x: px(69), y: px(53), w: 96, variant: 1 },
  { kind: 'hc_store', x: px(77) + 8, y: px(53), w: 144 },
  { kind: 'hc_house', x: px(62), y: px(60), w: 96, variant: 2 },
  { kind: 'hc_house', x: px(69), y: px(60), w: 96, variant: 3 },
  { kind: 'hc_house', x: px(76), y: px(60), w: 96, variant: 4 },
  ...[[59, 54], [61, 54], [67, 54], [70, 61], [74, 54], [60, 61], [77, 61]].map(([tx, ty], i) => scooter(px(tx) + 8, px(ty) + 8, i)),
  betel(81, 57, 3), betel(82, 59, 5), tree(80, 61),
  { kind: 'hc_waterTower', x: px(82), y: px(52), w: 20 },
  // Village life: potted plants by the doors, laundry, a cat, a bench under the tree.
  ...[[65, 53], [72, 53], [65, 60], [72, 60], [79, 60], [58, 50], [58, 57]].map(([tx, ty]): Decor => ({ kind: 'pottedPlant', x: px(tx) + 8, y: px(ty) + 12, solid: { x: px(tx) + 3, y: px(ty) + 6, w: 10, h: 6 } })),
  { kind: 'laundryRack', x: px(63), y: px(55) + 12 },
  { kind: 'laundryRack', x: px(70), y: px(62) + 8 },
  { kind: 'catBasket', x: px(67) + 8, y: px(54) + 12 },
  { kind: 'hc_bench', x: px(80) + 8, y: px(62) + 6, w: 28, solid: { x: px(80), y: px(62) + 2, w: 28, h: 4 } },
  { kind: 'trashBin', x: px(81) + 8, y: px(54) + 6 },
  { kind: 'vendingDeco', x: px(82) + 8, y: px(53) + 12, solid: { x: px(82) + 1, y: px(53) + 4, w: 14, h: 8 } },
  { kind: 'flowerbed', x: px(24), y: px(62) },
  { kind: 'flowerbed', x: px(34), y: px(62) },
  tree(22, 60), tree(36, 60, 'cherry'),
  lamp(58, 54), lamp(72, 54), lamp(58, 61), lamp(72, 61), lamp(37, 55), lamp(21, 55),
];

// --- Art pass: people, vehicles, trees, signs, clutter and small animations per zone ------------
const person = (tx: number, ty: number, v: number, dx = 8, dy = 12): Decor => ({ kind: 'xm_person', x: px(tx) + dx, y: px(ty) + dy, variant: v });
const traveler = (tx: number, ty: number, v: number): Decor => ({ kind: 'hc_traveler', x: px(tx) + 8, y: px(ty) + 12, w: 32, variant: v });
const pot = (tx: number, ty: number, v: number): Decor => ({ kind: 'hc_flowerPot', x: px(tx) + 8, y: px(ty) + 12, w: 30, variant: v, solid: { x: px(tx) + 1, y: px(ty) + 6, w: 14, h: 6 } });
const flame = (tx: number, ty: number): Decor => ({ kind: 'hc_flamboyant', x: px(tx) + 8, y: px(ty) + 12, w: 76, solid: { x: px(tx) + 4, y: px(ty) + 7, w: 8, h: 5 } });
const banner = (tx: number, ty: number, v: number): Decor => ({ kind: 'hc_banner', x: px(tx) + 8, y: px(ty) + 12, w: 20, variant: v, solid: { x: px(tx) + 6, y: px(ty) + 9, w: 4, h: 3 } });
const sitters = (tx: number, ty: number, v: number): Decor => ({ kind: 'hc_sitters', x: px(tx) + 8, y: px(ty) + 12, w: 36, variant: v, solid: { x: px(tx) - 7, y: px(ty) + 2, w: 30, h: 10 } });
const cables = (tx: number, ty: number, w = 8, v = 0): Decor => ({ kind: 'hc_cables', x: px(tx) + px(w) / 2, y: px(ty) + 14, w: px(w), variant: v });
const shed = (tx: number, ty: number, v: number): Decor => ({ kind: 'hc_tinRoof', x: px(tx) + 8, y: px(ty) + 14, w: 40, variant: v, solid: { x: px(tx) - 8, y: px(ty) - 4, w: 32, h: 18 } });
const puddle = (tx: number, ty: number, w = 26): Decor => ({ kind: 'hc_puddle', x: px(tx) + 8, y: px(ty) + 8, w });
const neon = (tx: number, ty: number, v: number): Decor => ({ kind: 'hc_neon', x: px(tx) + 8, y: px(ty) + 12, w: 16, variant: v });
const bulbs = (tx: number, ty: number, w: number): Decor => ({ kind: 'hc_bulbs', x: px(tx) + px(w) / 2, y: px(ty) + 8, w: px(w) });
const scooterRow = (tx: number, ty: number, w: number, v = 0): Decor => ({ kind: 'xy_scooterRow', x: px(tx) + px(w) / 2, y: px(ty) + 14, w: px(w), variant: v, solid: { x: px(tx) + 2, y: px(ty) + 4, w: px(w) - 4, h: 10 } });

decor.push(
  // Station plaza: travellers, the shuttle queue, taxis, rental bikes, scooters, trees, pots, signs.
  traveler(10, 14, 1), traveler(14, 16, 6), traveler(21, 16, 10), traveler(24, 14, 13), traveler(28, 15, 22), traveler(16, 18, 17), traveler(7, 16, 3), traveler(12, 19, 8),
  person(19, 17, 5), person(26, 18, 14), person(8, 18, 21), person(15, 13, 9), person(23, 19, 18), person(30, 18, 1),
  { kind: 'hc_queue', x: px(27) + 8, y: px(18) + 4, w: 56, variant: 1 },
  ...[10, 12].map((tx, i): Decor => ({ kind: 'hc_taxi', x: px(tx) + 8, y: px(20) + 4, w: 30, variant: (i + 1) % 3, solid: { x: px(tx) - 4, y: px(19) + 10, w: 24, h: 10 } })),
  { kind: 'xy_taxi', x: px(14) + 8, y: px(20) + 6, w: 40, variant: 1, solid: { x: px(13) + 4, y: px(19) + 10, w: 32, h: 10 } },
  ...[2, 3, 4].map((tx, i): Decor => ({ kind: 'xy_bicycle', x: px(tx) + 8 + i * 4, y: px(16) + 10, w: 20, variant: i })),
  scooterRow(29, 13, 5, 2), scooterRow(1, 11, 1, 0),
  flame(9, 13), flame(27, 13), flame(12, 21), flame(32, 21),
  pot(11, 15, 0), pot(25, 15, 1), pot(16, 19, 2), pot(21, 19, 3), pot(6, 18, 1), pot(30, 19, 0),
  banner(2, 15, 0), banner(33, 19, 1), banner(14, 19, 2), banner(22, 19, 0),
  { kind: 'hc_welcome', x: px(31) + 8, y: px(11) + 14, w: 108, solid: { x: px(28) + 6, y: px(11) + 8, w: 96, h: 6 } },
  { kind: 'hc_snackCart', x: px(4) + 8, y: px(14) + 12, w: 44, variant: 0, solid: { x: px(3) + 8, y: px(14), w: 32, h: 10 } },
  { kind: 'hc_snackCart', x: px(33) + 8, y: px(20) + 10, w: 44, variant: 1, solid: { x: px(32) + 8, y: px(19) + 14, w: 32, h: 10 } },
  { kind: 'xy_pigeons', x: px(16) + 8, y: px(17) + 8, w: 40, variant: 1 },
  { kind: 'xy_pigeons', x: px(24) + 8, y: px(18) + 8, w: 40, variant: 2 },
  sitters(6, 16, 0), sitters(29, 16, 1),
  { kind: 'hc_dog', x: px(10) + 8, y: px(18) + 10, w: 20 },
  puddle(13, 14, 30), puddle(8, 17, 26), puddle(26, 17, 30),
  { kind: 'hc_manhole', x: px(17) + 8, y: px(15) + 8, w: 16 },
  { kind: 'hc_manhole', x: px(29) + 8, y: px(14) + 8, w: 16 },
  { kind: 'trashBin', x: px(7) + 8, y: px(13) + 12 },
  { kind: 'trashBin', x: px(28) + 8, y: px(19) + 12 },
  cables(2, 21, 16, 1), cables(18, 21, 16, 0), cables(34, 21, 2, 1),

  // Downtown: scooters, neon, cables, people under the arcades, a dog, sheds in the back lane.
  scooterRow(4, 30, 4, 1), scooterRow(16, 30, 5, 3), scooterRow(28, 40, 4, 2),
  neon(13, 30, 0), neon(25, 30, 4), neon(1, 40, 2), neon(13, 40, 1), neon(25, 40, 3),
  person(8, 31, 2), person(19, 31, 7), person(29, 31, 12), person(5, 41, 19), person(17, 41, 24), person(3, 33, 30), person(22, 33, 35), person(10, 43, 42),
  sitters(30, 42, 2), { kind: 'hc_dog', x: px(6) + 8, y: px(34) + 4, w: 20, variant: 1 },
  cables(1, 25, 16, 0), cables(17, 25, 16, 1), cables(1, 45, 16, 1), cables(17, 45, 16, 0),
  puddle(7, 32, 24), puddle(20, 42, 26), { kind: 'hc_manhole', x: px(12) + 8, y: px(33) + 8, w: 16 },
  { kind: 'hc_truck', x: px(30) + 8, y: px(44) + 6, w: 48, solid: { x: px(29) + 4, y: px(43) + 12, w: 40, h: 10 } },
  { kind: 'xy_cafeTables', x: px(21) + 8, y: px(42) + 12, w: 40 },

  // Dongdamen: bulb strings, neon, sheds along the back, more people, stools, puddles.
  bulbs(40, 30, 12), bulbs(58, 30, 12), bulbs(40, 36, 12), bulbs(58, 36, 12), bulbs(40, 42, 12), bulbs(58, 42, 12),
  neon(40, 28, 0), neon(70, 28, 1), neon(40, 34, 3), neon(70, 34, 5), neon(70, 40, 4),
  shed(43, 27, 0), shed(48, 27, 1), shed(63, 27, 2), shed(68, 27, 0),
  ...[[44, 32], [48, 34], [57, 32], [62, 34], [66, 32], [46, 38], [54, 36], [60, 38], [43, 42], [51, 44], [63, 40], [68, 44], [58, 40], [53, 29], [66, 29], [41, 44]].map(([tx, ty], i) => person(tx, ty, (i * 5 + 3) % 64)),
  ...[[47, 29], [61, 29], [55, 44]].map(([tx, ty], i) => sitters(tx, ty, i)),
  ...[[42, 37], [43, 37], [52, 43], [53, 43], [65, 42]].map(([tx, ty]): Decor => ({ kind: 'stool', x: px(tx) + 4, y: px(ty) + 8 })),
  { kind: 'hc_dog', x: px(45) + 8, y: px(40) + 4, w: 20 },
  puddle(50, 33, 28), puddle(62, 37, 24), puddle(45, 43, 26), { kind: 'hc_manhole', x: px(56) + 8, y: px(35) + 8, w: 16 },
  { kind: 'trashBin', x: px(60) + 4, y: px(31) + 10 }, { kind: 'trashBin', x: px(52) + 4, y: px(40) + 10 },
  cables(40, 45, 16, 1), cables(56, 45, 16, 0), cables(72, 45, 12, 1),
  cables(41, 21, 16, 0), cables(57, 25, 16, 1), cables(73, 25, 11, 0),

  // Pine Garden: stone lanterns on the gravel, flower pots, visitors, pigeons, railings over the river.
  { kind: 'hc_toro', x: px(63) + 8, y: px(17) + 4, w: 12, solid: { x: px(63) + 4, y: px(17), w: 8, h: 4 } },
  { kind: 'hc_toro', x: px(79) + 8, y: px(17) + 4, w: 12, solid: { x: px(79) + 4, y: px(17), w: 8, h: 4 } },
  { kind: 'hc_toro', x: px(78) + 8, y: px(9) + 4, w: 12, solid: { x: px(78) + 4, y: px(9), w: 8, h: 4 } },
  pot(64, 15, 1), pot(77, 15, 2),
  banner(62, 15, 4), banner(80, 15, 4),
  person(67, 16, 13), person(74, 16, 26), person(79, 7, 1), person(71, 19, 44), person(62, 13, 5),
  sitters(76, 19, 1), sitters(61, 19, 2),
  { kind: 'xy_pigeons', x: px(69) + 8, y: px(16) + 10, w: 44, variant: 3 },
  { kind: 'hc_railing', x: px(63), y: px(5) + 4, w: 48 }, { kind: 'hc_railing', x: px(72), y: px(5) + 4, w: 48 }, { kind: 'hc_railing', x: px(81), y: px(5) + 4, w: 32 },
  ...[[62, 8], [64, 11], [77, 11], [82, 8], [60, 17], [84, 12], [66, 20], [79, 20]].map(([tx, ty], i): Decor => ({ kind: 'tk_fern', x: px(tx) + 8, y: px(ty) + 12, variant: i % 3 })),
  { kind: 'bushDeco', x: px(62) + 8, y: px(19) + 12 }, { kind: 'bushDeco', x: px(83) + 8, y: px(16) + 12 },
  { kind: 'flowerbed', x: px(68), y: px(19) + 12 }, { kind: 'flowerbed', x: px(74), y: px(19) + 12 },
  { kind: 'hc_bench', x: px(66) + 8, y: px(18) + 2, w: 28, solid: { x: px(66) + 2, y: px(17) + 14, w: 28, h: 4 } },
  { kind: 'hc_dog', x: px(73) + 8, y: px(17) + 14, w: 20, variant: 1 },
  // Creative park: scooters by the warehouses, a flame tree, people and pots.
  scooterRow(42, 19, 4, 1), flame(49, 13), pot(43, 12, 3), pot(55, 12, 0), person(51, 16, 25), person(45, 18, 38), sitters(52, 19, 0),

  // Beibin Park and the coast: palms, pots, pigeons, sitters on the seafront, bikes.
  pot(74, 36, 1), pot(80, 36, 2), sitters(78, 42, 1), person(84, 30, 10), person(85, 44, 3), person(84, 18, 7),
  ...[[85, 10], [85, 38], [85, 57]].map(([tx, ty], i): Decor => ({ kind: 'xy_bicycle', x: px(tx) + 8, y: px(ty) + 10, w: 20, variant: i })),
  { kind: 'hc_railing', x: px(87), y: px(22) + 4, w: 30 }, { kind: 'hc_railing', x: px(87), y: px(42) + 4, w: 30 }, { kind: 'hc_railing', x: px(87), y: px(5) + 4, w: 30 },
  flame(73, 44), flame(81, 44),

  // Ji'an: the café terrace, the farm truck, sheds and cables, village elders, pots and a dog.
  { kind: 'xy_cafeTables', x: px(27) + 8, y: px(58) + 12, w: 40 }, { kind: 'xy_cafeTables', x: px(31) + 8, y: px(58) + 12, w: 40 },
  person(26, 57, 2), person(33, 57, 15), person(29, 59, 20), sitters(23, 60, 1),
  { kind: 'hc_truck', x: px(36) + 8, y: px(53) + 8, w: 48, solid: { x: px(35) + 4, y: px(52) + 14, w: 40, h: 10 } },
  pot(22, 56, 0), pot(36, 56, 2), pot(38, 61, 1),
  cables(21, 48, 16, 0), cables(37, 48, 16, 1), cables(53, 48, 16, 0), cables(69, 48, 14, 1),
  cables(58, 55, 16, 1), cables(74, 55, 8, 0), cables(58, 62, 16, 0), cables(74, 62, 8, 1),
  shed(59, 51, 1), shed(76, 58, 2), shed(2, 50, 0),
  { kind: 'hc_truck', x: px(75) + 8, y: px(55) + 8, w: 48, variant: 1, solid: { x: px(74) + 4, y: px(54) + 14, w: 40, h: 10 } },
  sitters(66, 55, 2), sitters(73, 62, 0), person(60, 55, 36), person(69, 62, 41), person(79, 55, 9),
  { kind: 'hc_dog', x: px(63) + 8, y: px(62) + 4, w: 20 }, { kind: 'hc_dog', x: px(14) + 8, y: px(57) + 12, w: 20, variant: 1 },
  { kind: 'xy_laundry', x: px(66) + 8, y: px(56) + 12, w: 18 },
  pot(68, 56, 3), pot(75, 56, 1),
  { kind: 'xy_pigeons', x: px(24) + 8, y: px(56) + 12, w: 40, variant: 4 },
  // Qingxiu Temple: lanterns across the gate, worshippers with incense, ferns, a bench.
  { kind: 'lanternString', x: px(40), y: px(54) + 10, w: 240, variant: 1 },
  { kind: 'lanternString', x: px(40), y: px(60) + 8, w: 240, variant: 3 },
  person(46, 58, 64 + 5), person(48, 58, 64 + 9), person(43, 61, 64 + 17), person(51, 60, 12),
  banner(39, 54, 3), banner(55, 54, 3),
  ...[[40, 56], [54, 56], [40, 61], [54, 61]].map(([tx, ty], i): Decor => ({ kind: 'tk_fern', x: px(tx) + 8, y: px(ty) + 12, variant: i % 3 })),
  sitters(47, 62, 2), pot(41, 58, 2), pot(53, 58, 2),
);

const L = (tx: number, ty: number, r: number, color: string): Light => ({ x: px(tx), y: px(ty), r, color });
const WARM = '255,224,150';
const lights: Light[] = [
  // Lamps.
  ...decor.filter((d) => d.kind === 'lamp').map((d) => ({ x: d.x, y: d.y - 30, r: 60, color: WARM })),
  // Station hall glow and the name on the roof.
  L(18, 9, 150, '255,240,210'), L(18, 5, 90, '255,120,90'),
  // Night market: lanterns and stall bulbs.
  ...[42, 46, 50, 60, 64, 68].map((tx) => L(tx, 29, 56, '255,170,90')),
  ...[42, 46, 50].map((tx) => L(tx, 35, 52, '255,190,110')),
  L(55, 25, 90, '255,90,70'), L(55, 33, 80, '255,170,90'), L(55, 40, 80, '255,170,90'), L(48, 42, 70, '255,200,140'),
  L(65, 34, 100, '200,140,255'), L(62, 41, 60, '255,170,90'),
  // Hotel windows and sign; container coffee; temple lanterns; the village store.
  L(77, 32, 100, '255,230,180'), L(80, 28, 60, '120,220,255'),
  L(29, 52, 120, '255,236,190'), L(25, 58, 40, WARM), L(36, 58, 40, WARM),
  ...[56, 59, 62].flatMap((ty) => [L(45, ty, 34, '255,200,120'), L(49, ty, 34, '255,200,120')]),
  L(47, 53, 60, '255,210,150'),
  L(77, 52, 90, '200,255,230'),
  L(71, 13, 90, '255,230,190'), L(45, 9, 60, WARM), L(54, 9, 60, WARM), L(53, 17, 50, WARM),
  L(14, 33, 70, '255,200,140'), L(26, 33, 70, '255,200,140'), L(8, 28, 50, '255,140,110'), L(20, 38, 50, '255,140,110'), L(30, 28, 50, '120,200,255'),
  // Art pass: bulb strings over the market lanes, neon, the snack carts, the plaza billboard, sheds.
  ...[[46, 29], [64, 29], [46, 35], [64, 35], [46, 41], [64, 41]].map(([tx, ty]) => L(tx, ty, 70, '255,225,160')),
  ...[[40, 27], [70, 27], [40, 33], [70, 33], [70, 39], [13, 29], [25, 29], [1, 39], [13, 39], [25, 39]].map(([tx, ty]) => L(tx, ty, 34, '255,120,160')),
  L(4, 13, 40, '255,236,190'), L(33, 19, 40, '255,236,190'), L(31, 9, 70, '255,240,220'),
  L(29, 52, 40, WARM), L(66, 56, 30, WARM), L(73, 62, 30, WARM),
];

export const hlcity: MapDef = {
  id: 'hlcity',
  w: b.w,
  h: b.h,
  rows: b.rows(),
  indoor: false,
  theme: { wallFace: '#e9dcc3', wallLight: '#f6ecd8', wainscot: '#8a5c3a', wallTop: '#3a2a20' },
  doors: [{ tiles: [[77, 35]], to: 'hotel', arrive: { x: px(7), y: px(8) + 6, facing: 'up' } }],
  decor,
  areas: [
    { key: 'hcStation', rect: { x: 0, y: 0, w: px(35), h: px(21) } },
    { key: 'hcCreative', rect: { x: px(40), y: px(5), w: px(18), h: px(16) } },
    { key: 'hcPine', rect: { x: px(58), y: px(5), w: px(26), h: px(16) } },
    { key: 'hcMeilun', rect: { x: px(40), y: 0, w: px(46), h: px(5) } },
    { key: 'hcSnacks', rect: { x: 0, y: px(26), w: px(35), h: px(19) } },
    { key: 'hcIndigenous', rect: { x: px(58), y: px(26), w: px(13), h: px(8) } },
    { key: 'hcMainland', rect: { x: px(40), y: px(26), w: px(13), h: px(8) } },
    { key: 'hcDongdamen', rect: { x: px(40), y: px(26), w: px(31), h: px(19) } },
    { key: 'hcHotel', rect: { x: px(72), y: px(26), w: px(12), h: px(12) } },
    { key: 'hcBike', rect: { x: px(84), y: 0, w: px(12), h: px(H) } },
    { key: 'hcBeibin', rect: { x: px(72), y: px(5), w: px(24), h: px(44) } },
    { key: 'hcContainer', rect: { x: px(21), y: px(49), w: px(17), h: px(14) } },
    { key: 'hcQingxiu', rect: { x: px(39), y: px(49), w: px(17), h: px(14) } },
    { key: 'hcPaddies', rect: { x: 0, y: px(49), w: px(21), h: px(15) } },
    { key: 'hcJian', rect: { x: 0, y: px(49), w: px(86), h: px(15) } },
    { key: 'hlcity', rect: { x: 0, y: 0, w: px(W), h: px(H) } },
  ],
  lights,
  spots: {
    train: { x: px(18), y: px(13) + 10, facing: 'down' },
    shuttle: { x: px(31), y: px(17) + 4, facing: 'up' },
    hotelDoor: { x: px(77) + 8, y: px(36) + 10, facing: 'down' },
  },
};
